/**
 * tagColors.js — SINGLE SOURCE OF TRUTH for the tag colour allowlist.
 *
 * Tag pills can carry a colour. The server stores a *colour-class hint* (e.g.
 * 'tag-teal'), never raw CSS. We render that hint as an extra className ONLY if
 * it is in this allowlist — so a poisoned/unknown value can never become inline
 * style or arbitrary markup. Unknown/empty → the neutral default pill.
 *
 * Everything downstream derives from the ONE `TAG_COLOURS` list below:
 *   • TAG_COLOUR_CLASSES (the allowlist Set) — derived, not hand-maintained.
 *   • tagColourClass()   — maps a stored hint to a safe `.tag-c-*` suffix.
 *   • the `.tag-pill.tag-c-*` / `.tag-swatch.tag-c-*` CSS rules — generated at
 *     runtime by injectTagColourStyles() from the SAME list (see main.jsx), so
 *     adding a colour is exactly ONE edit here.
 *
 * The api mirror lives in api/src/lib/tagDto.js (separate package). The frontend
 * cross-package pin test in test/tagColors.test.js imports BOTH allowlists (this
 * one and tagDto.js's, via the @shared loader) and asserts they are the SAME set,
 * so the two can never silently drift.
 */

/**
 * The palette. To add a colour: add ONE row here. `fill` is the soft pill
 * background/border/text; `swatch` is the solid dot used in the picker menu.
 */
export const TAG_COLOURS = [
  { name: 'teal',   fill: { bg: '#CCFBF1', fg: '#0F766E', border: '#5EEAD4' }, swatch: '#14B8A6' },
  { name: 'blue',   fill: { bg: '#E8F1FE', fg: '#1A56C4', border: '#B6D5FB' }, swatch: '#2E7DE0' },
  { name: 'violet', fill: { bg: '#EDE9FE', fg: '#5B21B6', border: '#C4B5FD' }, swatch: '#7C3AED' },
  { name: 'amber',  fill: { bg: '#FEF3C7', fg: '#92520A', border: '#FCD34D' }, swatch: '#D97706' },
  { name: 'rose',   fill: { bg: '#FFE4E6', fg: '#9F1239', border: '#FDA4AF' }, swatch: '#E11D48' },
  { name: 'slate',  fill: { bg: '#F1F5F9', fg: '#334155', border: '#CBD5E1' }, swatch: '#64748B' },
];

/**
 * The full allowlist of stored hint values — the per-colour `tag-<name>` keys
 * plus the reserved `tag-system` look. DERIVED from TAG_COLOURS so it can never
 * drift from the palette. Used by tagColourClass() (and exported for tests).
 */
export const TAG_COLOUR_CLASSES = new Set([
  ...TAG_COLOURS.map(c => `tag-${c.name}`),
  'tag-system',   // reserved look for system tags (base modifier, no .tag-c-*)
]);

/**
 * Resolve a tag's stored colour hint to a safe pill className suffix.
 * @returns {string} e.g. ' tag-c-teal', or '' when no/unknown colour.
 */
export function tagColourClass(colour) {
  if (!colour || !TAG_COLOUR_CLASSES.has(colour)) return '';
  // 'tag-system' is already a base modifier class; the rest map to .tag-c-*.
  return colour === 'tag-system' ? '' : ` tag-c-${colour.replace(/^tag-/, '')}`;
}

// A custom colour is a strict 6-digit hex (mirrors TAG_HEX_RE in tagDto.js). Only
// a validated hex is ever interpolated into a style, so it can't inject CSS.
const TAG_HEX_RE = /^#[0-9a-fA-F]{6}$/;
export const isHexColour = (c) => typeof c === 'string' && TAG_HEX_RE.test(c);

/**
 * Inline style for a custom-hex tag PILL — soft tint derived from the hex via
 * color-mix. Returns undefined for palette classes / no colour (those use the
 * `.tag-c-*` class from tagColourClass instead).
 */
export function tagPillStyle(colour) {
  if (!isHexColour(colour)) return undefined;
  return {
    background:   `color-mix(in srgb, ${colour} 15%, white)`,
    color:        `color-mix(in srgb, ${colour} 72%, black)`,
    borderColor:  `color-mix(in srgb, ${colour} 40%, white)`,
  };
}

/** Inline style for a custom-hex SWATCH dot (solid colour). */
export function tagSwatchStyle(colour) {
  return isHexColour(colour) ? { background: colour } : undefined;
}

/**
 * Build the CSS text for every palette colour from TAG_COLOURS. Pure + exported
 * so it can be unit-tested without a DOM. The pill rule tints fill/text/border;
 * the swatch rule is the solid dot in the picker.
 */
export function buildTagColourCss() {
  return TAG_COLOURS.map(c => {
    const { bg, fg, border } = c.fill;
    return (
      `.tag-pill.tag-c-${c.name}{background:${bg};color:${fg};border-color:${border};}` +
      `.tag-swatch.tag-c-${c.name}{background:${c.swatch};}`
    );
  }).join('\n');
}

/**
 * Inject the generated `.tag-c-*` rules once. Called from the app entrypoint so
 * the palette in this file is the single place a colour is defined. Idempotent.
 */
export function injectTagColourStyles(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc) return;
  const ID = 'tag-colour-palette';
  if (doc.getElementById(ID)) return;
  const style = doc.createElement('style');
  style.id = ID;
  style.textContent = buildTagColourCss();
  doc.head.appendChild(style);
}
